import React, { useState, useEffect } from 'react';

interface Brand {
    Name: string;
    DimensionId: string;
    UrlSlug: string;
}

interface GroupedBrands {
    [key: string]: Brand[];
}

const customSort = (a: Brand, b: Brand) => {
    if (a.Name && b.Name) {
        return a.Name.toLowerCase().localeCompare(b.Name.toLowerCase());
    }
    if (a.Name && !b.Name) {
        return -1;
    }
    if (!a.Name && b.Name) {
        return 1;
    }
    return 0;
};

const groupBrandsByFirstLetter = (brands: Brand[]): GroupedBrands => {
    const numberGroupKey = '#';
    return brands.reduce((groups, brand) => {
        let letter = brand.Name[0]
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        if (!/^[A-Za-z]/.test(letter)) {
            letter = numberGroupKey;
        }
        if (!groups[letter]) {
            groups[letter] = [];
        }
        groups[letter].push(brand);
        return groups;
    }, {} as GroupedBrands);
};

export const BrandsList = ({ brandsData, category }: { brandsData: Brand[]; category: string }) => {
    const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({});
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const groupedData = groupBrandsByFirstLetter(brandsData.sort(customSort));

    const sortedGroupKeys = Object.keys(groupedData).sort((a, b) => {
        if (a === '#') return 1;
        if (b === '#') return -1;
        return a.localeCompare(b);
    });

    const toggleGroup = (letter: string) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [letter]: !prev[letter],
        }));
    };

    return (
        <>
            {sortedGroupKeys.map((letter) => (
                <div key={letter}>
                    <h3 id={`group-${letter}`} className="pb-5 pt-6 text-4xl font-bold md:text-3xl">
                        {letter}
                    </h3>
                    <div className="flex flex-wrap">
                        {groupedData[letter].map((brand, index) => {
                            const isExpanded = expandedGroups[letter];
                            if (windowWidth < 768 && !isExpanded && index >= 9) return null;

                            const isLastVisibleItem = windowWidth < 768 && !isExpanded && index === 8;

                            return (
                                <div
                                    key={brand.DimensionId}
                                    className="box-border w-full pb-6 md:w-1/3 md:pb-4 lg:w-1/4"
                                >
                                    <a
                                        href={
                                            `/products/brand/${brand.UrlSlug}` +
                                            (category ? `?category=${category}` : '')
                                        }
                                        className={`text-[13px] font-light md:text-base ${
                                            isLastVisibleItem && groupedData[letter].length > 9 ? 'text-black/20' : ''
                                        } ${
                                            isLastVisibleItem && groupedData[letter].length > 9
                                                ? 'bg-gradient-to-b from-black/30 to-white/60 bg-clip-text text-transparent'
                                                : ''
                                        }`}
                                    >
                                        {brand.Name}
                                    </a>
                                </div>
                            );
                        })}
                    </div>
                    {windowWidth < 768 && groupedData[letter].length > 9 && (
                        <button
                            className={`text-xs underline ${expandedGroups[letter] ? 'less' : 'more'}`}
                            onClick={() => toggleGroup(letter)}
                        >
                            {expandedGroups[letter] ? 'Read Less' : 'Read More'}
                        </button>
                    )}
                </div>
            ))}
        </>
    );
};
